import apiRoutes from '@constants/apiRoutes';
import { DataAccessProvider } from '@constants/constants';
import baseApi from '@services/api';
import {
  BasicGameApiResponse,
  DealItem,
  DealsCsvDownloadResponse,
  DealsListingResponse,
  DealsListingResponseV2,
  GamesFilterOptionsResponse,
} from './types/games';
import { ApiResponse, PaginatedResponse } from './types/common';
import { AuditLogResponseObject } from '../pages/Dashboard/components/DealSetup/components/DealAuditLogs/types';

export type DealSetupLastUpdatedDetails = {
  updatedByEmail: string;
  updatedBy: string;
  auditedAt: string;
} | null;

export interface GameDetailsPayload {
  name: string;
  dealName: string | null;
  refreshRequired: boolean;
  clientCode: string;
  signingAuthority: string | null;
  signingAuthorityEmail: string | null;
  signingAuthorityTitle: string | null;
  genre: string | null;
  category: string | null;
  dataSource: DataAccessProvider;
  status: string | null;
  metadata: string | null;
  financialYearEndDate: string | null;
  pocEmails: string | null;
  pvxPocEmail?: string;
  pvxPocUser?: string;
  pvxPocName?: string;
  secondaryPvxPocs?: string[];
  pipedriveDealId?: number | null;
  dealSource?: string | null;
}

export interface GameDetailsResponse {
  data: GameDetails;
  code: number;
  message: string;
}

export interface ThresholdItem {
  type: 'CUMULATIVE_ROAS' | 'INCREMENTAL_ROAS';
  periodType: 'DAILY' | 'MONTHLY';
  periodValue: number | null;
  name: string | null;
  value: number;
}

export interface DealStructureWithThresholds {
  dealStructureId: string;
  startCohort: string;
  endCohort: string;
  status: string;
  thresholds: ThresholdItem[];
}

export type GameThresholdsResponse = ApiResponse<DealStructureWithThresholds[]>;

interface GameDetails {
  id: string;
  clientCode: string;
  gameId: string;
  name: string;
  dealName: string;
  companyName: string;
  address: string;
  signingAuthority: string;
  signingAuthorityEmail: string;
  signingAuthorityTitle: string;
  status: string; // Replace or extend union type if there are other possible statuses
  category: string;
  genre: string;
  metadata: string;
  refreshRequired: boolean;
  financialYearEndDate: string;
  companyId: string;
  companyHQ?: string;
  pocEmails?: string[];
  pvxPocEmail?: string;
  pvxPocUser?: string;
  pvxPocName?: string;
  secondaryPvxPocs?: string[];
  pipedriveDealId?: number;
  dealSource?: string;
}

export interface LinkedPvxPoc {
  ownerId: number;
  emailId: string;
  userId: string;
  ownerName: string;
}

export interface PvxPoc extends LinkedPvxPoc {
  linkedPocs?: LinkedPvxPoc[];
}

export interface ExcludedAccountingRevenueDeal {
  gameId: string;
  gameName: string | null;
  dealName: string | null;
  companyName: string | null;
  clientCode: string;
  dataSource: string;
}

export interface PvxPocResponse {
  data: PvxPoc[];
  code: number;
  message: string;
}

const games = baseApi.injectEndpoints({
  endpoints: (build) => ({
    gameDetails: build.mutation<any, GameDetailsPayload>({
      query: (payload) => ({
        url: `${apiRoutes.games}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['companyList'],
    }),

    updateGameDetails: build.mutation<any, { gameId: string; body: GameDetailsPayload }>({
      query: (payload) => {
        return {
          url: `${apiRoutes.games}/${payload.gameId}`,
          method: 'PUT',
          body: payload.body,
        };
      },
      invalidatesTags: ['companyList'],
    }),

    getGames: build.query<DealsListingResponse, string>({
      query: (queryString: string) => `${apiRoutes.games}${queryString ? `?${queryString}` : ''}`,
    }),

    getGamesV2: build.query<DealsListingResponseV2, string>({
      query: (queryString: string) => `${apiRoutes.gamesV2}${queryString ? `?${queryString}` : ''}`,
    }),

    getGamesFilterOptions: build.query<ApiResponse<GamesFilterOptionsResponse>, void>({
      query: () => apiRoutes.gamesFilterOptions,
    }),
    getGamesFilterOptionsV2: build.query<ApiResponse<GamesFilterOptionsResponse>, void>({
      query: () => apiRoutes.gamesFilterOptionsV2,
    }),
    getGamesPaginated: build.infiniteQuery<PaginatedResponse<DealItem>, string, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.data.totalPages === lastPageParam + 1) return undefined;
          return lastPageParam + 1;
        },
      },

      query: ({ queryArg, pageParam }) => {
        const base = `${apiRoutes.games}?`;
        return `${base}${queryArg || ''}&pageNumber=${pageParam}`;
      },
    }),

    getGameById: build.query<GameDetailsResponse, string>({
      query: (gameId: string) => `${apiRoutes.games}/${gameId}`,
    }),

    getCategoriesAndGenres: build.query<any, void>({
      query: () => `${apiRoutes.categoriesAndGenres}`,
    }),

    autoCategoryClassify: build.mutation<
      any,
      {
        playstoreUrl?: string;
        appstoreUrl?: string;
        websiteUrl?: string;
      }
    >({
      query: (payload) => ({
        url: `${apiRoutes.autoCategoryClassify}`,
        method: 'POST',
        body: payload,
      }),
    }),

    autoCategoryClassifyJob: build.query<any, { jobId: string }>({
      query: ({ jobId }) => ({
        url: apiRoutes.autoCategoryClassifyJob(jobId),
        method: 'GET',
      }),
    }),

    addCategoryAndGenre: build.mutation<void, { category: string; genre: string }>({
      query: (payload) => ({
        url: `${apiRoutes.categoriesAndGenres}`,
        method: 'POST',
        body: payload,
      }),
    }),
    dealsCsvDownload: build.query<
      DealsCsvDownloadResponse,
      { refreshRequired?: boolean; search?: string }
    >({
      query: ({ refreshRequired, search }) => {
        const queryParams: string[] = [];

        queryParams.push(`sort=updatedAt`);
        queryParams.push(`sortOrder=DESC`);

        if (refreshRequired?.toString()) {
          queryParams.push(`refreshRequired=${refreshRequired}`);
        }

        if (search) {
          queryParams.push(`search=${encodeURIComponent(search)}`);
        }

        return apiRoutes.dealsCsvDownload(queryParams.join('&'));
      },
    }),
    dealsCsvDownloadV2: build.query<DealsCsvDownloadResponse, string>({
      query: (queryString: string) => apiRoutes.dealsCsvDownloadV2(queryString),
    }),

    basicGameDetails: build.query<
      ApiResponse<BasicGameApiResponse[]>,
      {
        status?: string | string[];
        page?: number;
        size?: number;
        sortBy?: string;
        sortOrder?: string;
        clientCode?: string;
        paginated?: boolean;
      }
    >({
      query: ({ status, page, size, sortBy, sortOrder, clientCode, paginated }) => {
        const queryParams = new URLSearchParams();

        if (paginated === false) {
          queryParams.set('paginated', 'false');
        }

        if (status) {
          const statuses = Array.isArray(status) ? status : [status];
          statuses.forEach((value) => {
            if (value) queryParams.append('status', value);
          });
        }

        if (clientCode) {
          queryParams.set('clientCode', clientCode);
        }

        if (paginated !== false) {
          if (page !== undefined && page !== null) {
            queryParams.set('pageNumber', String(page));
          }

          if (size !== undefined && size !== null) {
            queryParams.set('pageSize', String(size));
          }
        }

        if (sortBy) {
          queryParams.set('sort', sortBy);
        }

        if (sortOrder) {
          queryParams.set('sortOrder', sortOrder);
        }

        const queryString = queryParams.toString();
        return `${apiRoutes.basicGameDetails}${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getDealNames: build.query<
      ApiResponse<BasicGameApiResponse[]>,
      {
        status?: string;
        page?: number;
        size?: number;
        sortBy?: string;
        sortOrder?: string;
        clientCode?: string;
      }
    >({
      query: ({ status, page, size, sortBy, sortOrder, clientCode }) => {
        const queryParams = new URLSearchParams();

        if (status) {
          queryParams.set('status', status);
        }

        if (clientCode) {
          queryParams.set('clientCode', clientCode);
        }

        if (page !== undefined && page !== null) {
          queryParams.set('pageNumber', String(page));
        }

        if (size !== undefined && size !== null) {
          queryParams.set('pageSize', String(size));
        }

        if (sortBy) {
          queryParams.set('sort', sortBy);
        }

        if (sortOrder) {
          queryParams.set('sortOrder', sortOrder);
        }

        const queryString = queryParams.toString();
        return `${apiRoutes.getDealNames}${queryString ? `?${queryString}` : ''}`;
      },
    }),
    getGameThresholds: build.query<
      GameThresholdsResponse,
      { gameId: string; type?: 'CUMULATIVE_ROAS' | 'INCREMENTAL_ROAS' }
    >({
      query: ({ gameId, type }) => apiRoutes.getGameThresholds(gameId, type),
    }),

    auditLogs: build.query<ApiResponse<AuditLogResponseObject[]>, { gameId: string }>({
      query: ({ gameId }) => apiRoutes.auditLogs(gameId),
    }),

    dealSetupLastUpdatedDetails: build.query<
      ApiResponse<DealSetupLastUpdatedDetails>,
      { gameId: string }
    >({
      query: ({ gameId }) => apiRoutes.dealSetupLastUpdatedDetails(gameId),
    }),
    getPvxPoc: build.query<PvxPocResponse, void>({
      query: () => apiRoutes.getPvxPoc,
    }),
    getExcludedFromAccountingRevenue: build.query<
      ApiResponse<ExcludedAccountingRevenueDeal[]>,
      void
    >({
      query: () => apiRoutes.excludedFromAccountingRevenue,
    }),
  }),
});

export const {
  useGetGamesPaginatedInfiniteQuery,
  useLazyGetGamesQuery,
  useGameDetailsMutation,
  useUpdateGameDetailsMutation,
  useLazyGetGameByIdQuery,
  useLazyGetCategoriesAndGenresQuery,
  useAutoCategoryClassifyMutation,
  useLazyAutoCategoryClassifyJobQuery,
  useAddCategoryAndGenreMutation,
  useLazyDealsCsvDownloadQuery,
  useLazyDealsCsvDownloadV2Query,
  useBasicGameDetailsQuery,
  useLazyBasicGameDetailsQuery,
  useLazyGetDealNamesQuery,
  useLazyGetGameThresholdsQuery,
  useLazyAuditLogsQuery,
  useLazyDealSetupLastUpdatedDetailsQuery,
  useGetPvxPocQuery,
  useLazyGetPvxPocQuery,
  useLazyGetGamesV2Query,
  useLazyGetGamesFilterOptionsQuery,
  useLazyGetGamesFilterOptionsV2Query,
  useGetExcludedFromAccountingRevenueQuery,
} = games;
